import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import CourseDescription from '@components/course-landing-page/course-description'
import HowClassWorks from '@components/course-landing-page/how-class-works'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers, {
  StatisticsProps,
} from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import {
  COURSE_FIRST_FOLD,
  COURSE_NAMES,
  FAQ,
} from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { formatSeoConfig } from '@lib/seo'
import { defineMessage, Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import ExpandableActivities from '@components/course-landing-page/expandable-activities'
import CourseQuizzes from '@components/course-landing-page/quizzes'
import ExploreTools, {
  ToolCardData,
} from '@components/course-landing-page/explore-tools'
import CodingalSubscription from '@components/home/codingal-subscription'
import CodingalProductBlog from '@components/blog/product-blogs'
import AboutUsCard from '@components/reviews-and-testimonials/about-us-card'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { CODING_GRADE_4_5 } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['cgm'].trialCourseQuery}`

const STATISTICS: StatisticsProps[] = [
  {
    title: '🎓 1,000+',
    description: defineMessage({ message: 'Expert CS instructors' }),
    backgroundColor: 'bg-orange-200',
  },
  {
    title: '👨‍💻 7 years',
    description: defineMessage({ message: 'Average teaching experience' }),
    backgroundColor: 'bg-blue-50',
  },
  {
    title: '👩 91%',
    description: defineMessage({ message: 'Female faculty' }),
    backgroundColor: 'bg-yellow-100',
  },
  {
    title: '💻 500+',
    description: defineMessage({ message: 'Lessons and projects' }),
    backgroundColor: 'bg-green-50',
  },
]

const GRADE_4_5_TOOLS: ToolCardData[] = [
  {
    title: 'Code.org',
    description:
      'Create animations and games by giving characters step by step instructions.',
    image: '/images/courses/coding-for-kids/code-org.jpg',
  },
  {
    title: 'Scratch',
    description:
      'Drag and drop colorful blocks to build games, stories and animations. Built by MIT Media Lab.',
    image: '/images/courses/coding-for-kids/scratch.jpg',
  },
  {
    title: 'MIT App Inventor',
    description:
      'Design and build mobile apps using simple visual blocks and layouts.',
    image: '/images/courses/coding-for-kids/mit.jpg',
  },
  {
    title: 'Website Development',
    description:
      'Build webpages with HTML, CSS and Bootstrap including text, images and interactive elements.',
    image: '/images/courses/coding-for-kids/website.jpg',
  },
  {
    title: 'Python',
    description:
      'Write beginner Python code to create apps, games and creative projects.',
    image: '/images/courses/coding-for-kids/python.jpg',
  },
  {
    title: 'AI & Machine Learning',
    description:
      'Discover how AI works through beginner projects using images, text and voice.',
    image: '/images/courses/coding-for-kids/ai-ml.jpg',
  },
]

const CodingGrade45 = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }
  useStoreUTMParams()

  const seoConfig = formatSeoConfig({
    title: 'Online Coding Classes for Grades 4 & 5 (Ages 9-11) | LIVE 1-on-1',
    description:
      'Live online coding classes for 4th and 5th graders. Learn Scratch, Python, web development and beginner AI. STEM.org accredited. Book a free trial.',
    url: 'https://www.codingal.com/coding-for-kids/grade-4-5/',
    metaImage:
      'https://www.codingal.com/images/courses/coding-for-kids/grade-4-5/og.jpg',
  })

  return (
    <>
      <NextSeo {...seoConfig} />
      <FAQPageJsonLd
        mainEntity={FAQ[CODING_GRADE_4_5].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName={COURSE_FIRST_FOLD[CODING_GRADE_4_5].name}
        description={COURSE_FIRST_FOLD[CODING_GRADE_4_5].description}
        provider={{ '@type': 'Organization', name: 'Codingal' }}
        offers={{ '@type': 'Offer', category: 'Paid' }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT16H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={CODING_GRADE_4_5}
        urlQuery={urlQuery}
        isPlatformPage
        hideReadMore
        background="bg-white"
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={CODING_GRADE_4_5} />
          <RecommendedCourses
            courseCodes={['cgm', 'cp', 'cc', 'gd', 'ad', 'rc']}
            title="Coding courses for 4th and 5th graders"
            description={
              <>
                Grade 4 and 5 students can pick from six tracks. Complete
                beginners should start with AI and Coding Champion. Families
                wanting a full learning pathway should choose AI and Coding
                Grandmaster. Students with a specific interest can pick Game
                Development, App Development or Roblox Champion. Every course is
                live, project based and ends with a STEM.org accredited
                certificate.
              </>
            }
            showAllCoursesButton
          />
          <ExploreTools
            title="Which tools your child will use"
            description="Grade 4 and 5 students use six platforms that take them from block coding into real programming languages. All are free or free to learn, web based and introduced through guided projects. Fourth and fifth graders start with Scratch and Code.org and progress to Python and beginner web development with HTML and CSS."
            urlQuery={urlQuery}
            tools={GRADE_4_5_TOOLS}
            note="⭐ All six platforms are free or free to learn, web-based and introduced through guided projects. Your child can start from day one without any setup."
          />
          <CourseFeatures
            courseTitle={CODING_GRADE_4_5}
            title={
              <Trans>
                An engaging coding journey for kids in Grades 4 and 5
              </Trans>
            }
          />
          <Activities courseName={CODING_GRADE_4_5} urlQuery={urlQuery} />
          <Teachers
            urlQuery={urlQuery}
            title={<Trans>Learn from expert Computer Science teachers</Trans>}
            statistics={STATISTICS}
            description={
              <Trans>
                Codingal&apos;s Grade 4 and 5 teachers are trained Computer
                Science educators with 7 or more years of classroom experience.
                Every instructor completes structured onboarding before teaching
                and is evaluated by student and parent ratings. Teachers are
                equipped to handle the bridge from block coding to Python for
                4th and 5th graders.
                <br />
                Teachers are rated 4.9 out of 5 by parents and students. The
                Codingal curriculum is rated 4.8 out of 5. The company Net
                Promoter Score (NPS) is 86 as of Q1 2026.
              </Trans>
            }
          />
          <HowClassWorks courseName={CODING_GRADE_4_5} urlQuery={urlQuery} />
          <CodingForStudents
            title="Our coding courses for Grades 4 and 5 are designed for students ready to go beyond the basics"
            description="Fourth and fifth graders learn through hands-on projects that feel genuinely exciting. Using tools like Scratch, Python, App Lab, and MIT App Inventor, upper elementary students build games, apps, and beginner AI projects in live Codingal classes designed specifically for this age group. As 9 to 11 year olds grow in confidence, they also develop sharper logical thinking, stronger problem-solving skills, and the ability to break complex challenges into manageable steps, abilities that transfer directly into math and science at school."
            imagePath="/images/courses/coding-for-kids/grade-1-3/coding"
            imageWidth={213}
            imageHeight={219}
            reverse
          />
          <ExpandableActivities
            title="100+ coding projects designed for Grades 4 and 5"
            description="The Grade 4 and 5 project library includes more than 100 projects across Scratch, Python, web development, app building and beginner AI. Projects are graded by difficulty and mapped to each course's learning outcomes."
            courseTitle={CODING_GRADE_4_5}
            urlQuery={urlQuery}
          />
          <CodingalSubscription compact subject="ELA" urlQuery={urlQuery} />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
            subject="coding"
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description="Codingal's Grade 4 and 5 program is STEM.org accredited and built on proven educational frameworks including Bloom's Taxonomy, BIDE and STEAM. The curriculum is aligned with U.S. Common Core and international state standards, so every lesson is age appropriate, project based and mapped to real learning outcomes. Through hands-on activities, 4th and 5th graders build games, apps, webpages and AI projects that apply programming concepts to real problems. Backed by STEM.org's annual audits, the program gives children a strong, future ready foundation in STEM."
          />
          <CodingCertificate
            title="Unlock your skills and earn your coding certificate"
            description="In Codingal's Grade 4 and 5 track, students progress through structured modules that take them from beginner block coding to writing real code in Python and beyond. Fourth and fifth graders learn loops, conditionals, app design, Scratch projects, web basics with HTML and CSS and beginner AI, turning every lesson into a working project. On completing the program, students earn a STEM.org accredited certificate, a valuable credential for school portfolios and a strong early signal of real coding ability."
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our coding classes are built for upper elementary students ready to go further"
            description="Codingal's Grade 4 and 5 curriculum takes students from visual block coding to building real apps, games, webpages and beginner AI projects. We begin with guided challenges that introduce core programming concepts through hands-on, project based activities. As upper elementary students grow in confidence, they explore Python, web development and app design at a pace that keeps every lesson feeling exciting. With every project they complete, 4th and 5th graders build sharper logical thinking and the confidence to take on increasingly ambitious coding challenges."
            imagePath="/images/courses/ai-and-ml/ai-course-students"
            imageWidth={235}
            imageHeight={241}
          />
          <Investors isCoursePage urlQuery={urlQuery} />
          <Faq
            courseName={CODING_GRADE_4_5}
            title="Frequently asked questions about coding classes for Grades 4 and 5"
          />
          <CourseQuizzes courseName={CODING_GRADE_4_5} />
          <CodingalProductBlog
            urlQuery={urlQuery}
            isCoursePage
            category="coding-for-kids"
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={CODING_GRADE_4_5}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <AboutUsCard />
      <FooterBanner
        title="Live AI & coding classes for kids in Grades 4 and 5 - book a free trial today"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return { props: {} }
}

export default CodingGrade45
